import { CloseOutlined, Person2Outlined } from '@mui/icons-material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import CancelOutlineIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  type SelectChangeEvent,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  consumerAPI,
  type OfflineBookingAdditionalChargeTypeName,
  type OfflineBookingCompletePayload,
  type OfflineBookingCompletionStatus,
  type OfflineBookingDecision,
} from 'api/consumerAPI'
import { CurrencyCode, CurrencySymbols } from 'api/models/Common/JyrneyEnums'
import IconInfo from 'components/Bookings/Confirmation/IconInfo'
import DirectionsMap from 'components/Common/DirectionsMap/DirectionsMap'
import { format, formatDistanceStrict } from 'date-fns'
import useQueryParams from 'hooks/useQueryParams'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'

type OfflineDecision = OfflineBookingDecision

type OfflineBookingProgress =
  | 'Allocated'
  | 'Declined'
  | 'Confirmed'
  | 'Cancelled'
  | 'OnRoute'
  | 'Arrived'
  | 'Passenger_Picked_Up'
  | 'Passenger_Dropped_Off'
  | 'OnRoute_Passenger_On_Board'
  | 'Completed'
  | 'Supplier_Cancelled'
  | 'No_Show'
  | 'Driver_Allocated'
  | 'Manual_Reallocation_Pending'
  | 'Manual_Intervention_Required'

type OfflineBookingTrackingStatus =
  | 'Unallocated'
  | 'Awaiting_Dispatch'
  | 'Not_Dispatched'
  | 'Ontime'
  | 'Delayed'
  | 'Driver_Waiting'
  | 'In_Progress'
  | 'Attention_Needed'

type BookingDriver = {
  name?: string
  contactNumber?: string
  vehicle?: {
    colour?: string
    makeAndModel?: string
    registrationNumber?: string
  }
}

type BookingLocation = {
  formattedAddress?: string
  addressLine1?: string
  latitude?: number
  longitude?: number
  lat?: number
  lon?: number
}

type OfflineBookingData = {
  bookingReference?: string
  jobReference?: string
  progress?: OfflineBookingProgress
  when?: string
  pickup?: BookingLocation
  dropOff?: BookingLocation
  additionalStops?: BookingLocation[]
  specialInstructions?: string
  journeyTime?: number
  journeyDistance?: number
  tracking?: {
    progress?: OfflineBookingProgress
    status?: OfflineBookingTrackingStatus
    driver?: BookingDriver
  }
  leadPassenger?: passengerContact | null
  numberOfPassengers?: number | null
}

type passengerContact = {
  name: string
  contactNumber: string
}

type OfflinePriceForm = {
  price: string
  vat: string
}

type OfflineRouteEstimate = {
  estimatedJourneyTime: number | null
  estimatedJourneyDistance: number | null
}

type OfflineExtraForm = {
  additionalChargeTypeName: OfflineBookingAdditionalChargeTypeName
  price: OfflinePriceForm
}

type JourneyMarkerKind = 'origin' | 'destination'

type OfflineCompleteFormState = {
  status: OfflineBookingCompletionStatus
  currency: string
  driverPrice: OfflinePriceForm
  waitingPrice: OfflinePriceForm
  serviceCharge: OfflinePriceForm
  unknownExtras: OfflineExtraForm[]
  journeyDistance: string
  journeyTime: string
  co2Emmissions: string
}

type OfflinePriceFormErrors = Partial<Record<keyof OfflinePriceForm, string>>

type OfflineExtraFormErrors = {
  additionalChargeTypeName?: string
  price?: OfflinePriceFormErrors
}

type OfflineCompleteFormErrors = {
  status?: string
  currency?: string
  driverPrice?: OfflinePriceFormErrors
  waitingPrice?: OfflinePriceFormErrors
  serviceCharge?: OfflinePriceFormErrors
  unknownExtras?: OfflineExtraFormErrors[]
  journeyDistance?: string
  journeyTime?: string
  co2Emmissions?: string
}

type PriceSectionKey = 'driverPrice' | 'waitingPrice' | 'serviceCharge'
type JourneyMetricKey = 'journeyDistance' | 'journeyTime' | 'co2Emmissions'

const completionStatusOptions: Array<{
  value: OfflineBookingCompletionStatus
  label: string
}> = [
  { value: 'Completed', label: 'Completed' },
  { value: 'No_Show', label: 'No show' },
]

const additionalChargeTypeOptions: Array<{
  value: OfflineBookingAdditionalChargeTypeName
  label: string
}> = [
  { value: 'Parking', label: 'Parking' },
  { value: 'Toll', label: 'Toll' },
  { value: 'Other', label: 'Other' },
]

// const progressLabelLookup: Record<OfflineBookingProgress, string> = {
//   Allocated: 'Allocated',
//   Declined: 'Declined',
//   Confirmed: 'Looking for a driver',
//   Cancelled: 'Cancelled',
//   Driver_Allocated: 'Driver allocated',
//   OnRoute: 'Driver is on the way',
//   Arrived: 'Driver has arrived',
//   Passenger_Picked_Up: 'Passenger picked up',
//   OnRoute_Passenger_On_Board: 'On trip',
//   Passenger_Dropped_Off: 'Dropped off',
//   Completed: 'Completed',
//   Supplier_Cancelled: 'Cancelled',
//   Manual_Reallocation_Pending: 'Awaiting reallocation',
//   Manual_Intervention_Required: 'Awaiting allocation',
//   No_Show: 'No show',
// }

// const trackingStatusLabelLookup: Record<OfflineBookingTrackingStatus, string> =
//   {
//     Unallocated: 'Unallocated',
//     Awaiting_Dispatch: 'Awaiting dispatch',
//     Not_Dispatched: 'Not dispatched',
//     Ontime: 'On time',
//     Delayed: 'Delayed',
//     Driver_Waiting: 'Driver waiting',
//     In_Progress: 'In progress',
//     Attention_Needed: 'Attention needed',
//   }

const validDecisions: OfflineDecision[] = [
  'accept',
  'decline',
  'cancel',
  'complete',
]

const decisionLabelMap: Record<OfflineDecision, string> = {
  accept: 'Accept',
  decline: 'Decline',
  cancel: 'Cancel',
  complete: 'Complete',
}

const pageTitleMap: Record<OfflineDecision, string> = {
  accept: 'You have accepted the booking',
  decline: 'Decline this booking',
  cancel: 'Cancel the booking',
  complete: 'Action Required: Complete Form',
}

const decisionMessageMap: Record<OfflineDecision, string> = {
  accept:
    'Thank you for accepting the booking. We will confirm back to the client.',
  decline: '',
  cancel: '',
  complete:
    'Enter the final journey below, including pricing, distance and mark as Complete or No Show.',
}

const pageInstructionsMap: Record<OfflineDecision, string> = {
  accept:
    'This booking has been accepted. The client will be notified with a confirmation.',
  decline:
    'Review the booking details below and confirm if you wish to decline this booking.',
  cancel:
    'Review the booking details below and confirm if you wish to cancel this booking.',
  complete:
    'Enter the final journey details below, including pricing, distance and whether to mark it as Complete or No Show.',
}

const decisionColorMap: Record<OfflineDecision, { bg: string; color: string }> =
  {
    accept: { bg: 'rgba(46, 125, 50, 0.12)', color: '#2e7d32' },
    decline: { bg: 'rgba(211, 47, 47, 0.12)', color: '#d32f2f' },
    cancel: { bg: 'rgba(211, 47, 47, 0.12)', color: '#d32f2f' },
    complete: { bg: 'rgba(2, 136, 209, 0.12)', color: '#0288d1' },
  }

const createEmptyPriceForm = (): OfflinePriceForm => ({
  price: '',
  vat: '',
})

const createEmptyExtraForm = (): OfflineExtraForm => ({
  additionalChargeTypeName: 'Parking',
  price: createEmptyPriceForm(),
})

const createInitialCompleteFormState = (): OfflineCompleteFormState => ({
  status: 'Completed',
  currency: '',
  driverPrice: createEmptyPriceForm(),
  waitingPrice: createEmptyPriceForm(),
  serviceCharge: createEmptyPriceForm(),
  unknownExtras: [],
  journeyDistance: '',
  journeyTime: '',
  co2Emmissions: '',
})

const formatAddress = (location?: BookingLocation) => {
  return location?.formattedAddress || location?.addressLine1 || 'Not available'
}

const formatJourneyTime = (journeyTime?: number) => {
  if (!journeyTime) return 'Not available'
  return formatDistanceStrict(0, journeyTime * 1000)
}

const formatJourneyDistance = (journeyDistance?: number) => {
  if (journeyDistance == null) return 'Not available'
  return `${journeyDistance} miles`
}

const formatBookingWhen = (when?: string) => {
  if (!when) return 'Not available'
  return format(new Date(when), 'dd MMM yyyy, HH:mm')
}

const journeyMarkerStyles: Record<
  JourneyMarkerKind,
  { background: string; border: string; color: string; label: string }
> = {
  origin: {
    background: '#0b7a32',
    border: '#085924',
    color: '#ffffff',
    label: 'A',
  },
  destination: {
    background: '#3b51f6',
    border: '#0943a1',
    color: '#ffffff',
    label: 'B',
  },
}

// const formatEnumFallbackLabel = (value: string) =>
//   value
//     .replaceAll('_', ' ')
//     .replace(/([a-z])([A-Z])/g, '$1 $2')
//     .trim()

const hasRoute = (booking?: OfflineBookingData) =>
  Boolean(booking?.pickup && booking?.dropOff)

const getNumberFieldError = (
  value: string,
  label: string,
  options?: { integer?: boolean },
) => {
  if (!value.trim()) return `${label} is required`

  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return `${label} must be a number`
  if (numericValue < 0) return `${label} must be 0 or more`
  if (options?.integer && !Number.isInteger(numericValue)) {
    return `${label} must be a whole number`
  }

  return undefined
}

const isBlankPriceForm = (formValue: OfflinePriceForm) =>
  !formValue.price.trim() && !formValue.vat.trim()

const getOptionalNumberFieldError = (
  value: string,
  label: string,
  options?: { integer?: boolean },
) => {
  if (!value.trim()) return undefined

  return getNumberFieldError(value, label, options)
}

const getPriceFormErrors = (
  formValue: OfflinePriceForm,
  label: string,
): OfflinePriceFormErrors | undefined => {
  const errors: OfflinePriceFormErrors = {}

  const priceError = getNumberFieldError(formValue.price, `${label} amount`)
  const vatError = getNumberFieldError(formValue.vat, `${label} VAT`)

  if (priceError) errors.price = priceError
  if (vatError) errors.vat = vatError

  return Object.keys(errors).length > 0 ? errors : undefined
}

const getOptionalPriceFormErrors = (
  formValue: OfflinePriceForm,
  label: string,
): OfflinePriceFormErrors | undefined => {
  if (isBlankPriceForm(formValue)) return undefined

  return getPriceFormErrors(formValue, label)
}

const getCompleteFormErrors = (
  formValue: OfflineCompleteFormState,
): OfflineCompleteFormErrors => {
  const errors: OfflineCompleteFormErrors = {}

  if (!formValue.status) {
    errors.status = 'Status is required'
  }
  if (!formValue.currency.trim()) {
    errors.currency = 'Currency is required'
  }

  const driverPriceErrors = getPriceFormErrors(
    formValue.driverPrice,
    'Driver price',
  )
  const waitingPriceErrors = getOptionalPriceFormErrors(
    formValue.waitingPrice,
    'Waiting price',
  )
  const serviceChargeErrors = getOptionalPriceFormErrors(
    formValue.serviceCharge,
    'Service charge',
  )

  if (driverPriceErrors) errors.driverPrice = driverPriceErrors
  if (waitingPriceErrors) errors.waitingPrice = waitingPriceErrors
  if (serviceChargeErrors) errors.serviceCharge = serviceChargeErrors

  const journeyDistanceError = getOptionalNumberFieldError(
    formValue.journeyDistance,
    'Journey distance',
  )
  const journeyTimeError = getOptionalNumberFieldError(
    formValue.journeyTime,
    'Journey time',
    { integer: true },
  )
  const co2EmmissionsError = getOptionalNumberFieldError(
    formValue.co2Emmissions,
    'CO2 emissions',
  )

  if (journeyDistanceError) errors.journeyDistance = journeyDistanceError
  if (journeyTimeError) errors.journeyTime = journeyTimeError
  if (co2EmmissionsError) errors.co2Emmissions = co2EmmissionsError

  const extraErrors = formValue.unknownExtras.map((extra) => {
    const currentErrors: OfflineExtraFormErrors = {}
    const extraPriceErrors = getPriceFormErrors(
      extra.price,
      'Additional charge',
    )

    if (
      !additionalChargeTypeOptions.some(
        (option) => option.value === extra.additionalChargeTypeName,
      )
    ) {
      currentErrors.additionalChargeTypeName =
        'Additional charge type is invalid'
    }
    if (extraPriceErrors) {
      currentErrors.price = extraPriceErrors
    }

    return currentErrors
  })

  if (extraErrors.some((entry) => Object.keys(entry).length > 0)) {
    errors.unknownExtras = extraErrors
  }

  return errors
}

const buildPricePayload = (
  formValue: OfflinePriceForm,
  currency: string,
): OfflineBookingCompletePayload['data']['driverPrice'] => ({
  price: Number(formValue.price),
  currency: currency.trim(),
  vat: Number(formValue.vat),
})

const buildCompletePayload = (
  communicationReference: string,
  formValue: OfflineCompleteFormState,
): OfflineBookingCompletePayload => {
  const data: OfflineBookingCompletePayload['data'] = {
    communicationReference,
    status: formValue.status,
    driverPrice: buildPricePayload(formValue.driverPrice, formValue.currency),
  }

  if (!isBlankPriceForm(formValue.waitingPrice)) {
    data.waitingPrice = buildPricePayload(
      formValue.waitingPrice,
      formValue.currency,
    )
  }

  if (!isBlankPriceForm(formValue.serviceCharge)) {
    data.serviceCharge = buildPricePayload(
      formValue.serviceCharge,
      formValue.currency,
    )
  }

  if (formValue.unknownExtras.length > 0) {
    data.unknownExtras = formValue.unknownExtras.map((extra) => ({
      additionalChargeTypeName: extra.additionalChargeTypeName,
      price: buildPricePayload(extra.price, formValue.currency),
    }))
  }

  if (formValue.journeyDistance.trim()) {
    data.journeyDistance = Number(formValue.journeyDistance)
  }

  if (formValue.journeyTime.trim()) {
    data.journeyTime = Number(formValue.journeyTime)
  }

  if (formValue.co2Emmissions.trim()) {
    data.co2Emmissions = Number(formValue.co2Emmissions)
  }

  return {
    correlationId: crypto.randomUUID(),
    data,
  }
}

const DriverSummary = ({ driver }: { driver?: BookingDriver }) => {
  const vehicleSummary = [
    driver?.vehicle?.colour,
    driver?.vehicle?.makeAndModel,
  ]
    .filter(Boolean)
    .join(' ')
  const hasDriverDetails = Boolean(
    driver?.name ||
      driver?.contactNumber ||
      vehicleSummary ||
      driver?.vehicle?.registrationNumber,
  )

  if (!hasDriverDetails) return null

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.9rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mb: 2,
        }}
      >
        Driver details
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        }}
      >
        {driver?.name && (
          <IconInfo
            Icon={DirectionsCarOutlinedIcon}
            title="Driver"
            value={driver.name}
          />
        )}
        {driver?.contactNumber && (
          <IconInfo
            Icon={PhoneOutlinedIcon}
            title="Mobile"
            value={driver.contactNumber}
          />
        )}
        {vehicleSummary && (
          <IconInfo
            Icon={DirectionsCarOutlinedIcon}
            title="Vehicle"
            value={vehicleSummary}
          />
        )}
        {driver?.vehicle?.registrationNumber && (
          <IconInfo
            Icon={InfoOutlinedIcon}
            title="Registration"
            value={driver.vehicle.registrationNumber}
          />
        )}
      </Box>
    </Paper>
  )
}

const PassengerSummary = ({
  passengerData,
}: {
  passengerData?: passengerContact
}) => {
  const hasPassengerData = passengerData
    ? Object.keys(passengerData).length > 0
    : false
  console.log(passengerData)
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.9rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mb: 2,
        }}
      >
        Passenger information
      </Typography>
      <Box sx={{ display: 'flex' }}>
        {hasPassengerData ? (
          <IconInfo
            Icon={Person2Outlined}
            title="Name"
            value={passengerData?.name}
          />
        ) : (
          ''
        )}
        {hasPassengerData ? (
          <IconInfo
            Icon={PhoneOutlinedIcon}
            title="Phone Number"
            value={passengerData?.contactNumber}
          />
        ) : (
          ''
        )}
      </Box>
    </Paper>
  )
}

const JourneyMarkerIcon = ({
  kind,
  sx,
}: {
  kind: JourneyMarkerKind
  sx?: Record<string, unknown>
}) => {
  const markerStyle = journeyMarkerStyles[kind]

  return (
    <Box
      sx={{
        ...sx,
        width: 29,
        minWidth: 29,
        height: 29,
        borderRadius: '999px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 700,
        lineHeight: 1,
        flex: '0 0 auto',
        color: markerStyle.color,
        backgroundColor: markerStyle.background,
        border: `2px solid ${markerStyle.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
      }}
    >
      {markerStyle.label}
    </Box>
  )
}

const PickupMarkerIcon = ({ sx }: { sx?: Record<string, unknown> }) => (
  <JourneyMarkerIcon kind="origin" sx={sx} />
)

const DropoffMarkerIcon = ({ sx }: { sx?: Record<string, unknown> }) => (
  <JourneyMarkerIcon kind="destination" sx={sx} />
)

const CurrencySelectField = ({
  label,
  value,
  error,
  helperText,
  onChange,
}: {
  label: string
  value: string
  error?: boolean
  helperText?: string
  onChange: (nextValue: string) => void
}) => {
  const labelId = `${label.replace(/\s+/g, '-').toLowerCase()}-label`

  return (
    <FormControl fullWidth error={error}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        value={value}
        label={label}
        input={<OutlinedInput label={label} />}
        onChange={(event: SelectChangeEvent) => onChange(event.target.value)}
      >
        {Object.entries(CurrencyCode).map(([currencyCode]) => (
          <MenuItem key={currencyCode} value={currencyCode}>
            <Box sx={{ display: 'flex', gap: '5px' }}>
              <Box
                sx={{
                  display: 'flex',
                  width: '25px',
                  height: '25px',
                  padding: '3px',
                  backgroundColor: '#042938',
                  alignContent: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                }}
              >
                <Typography
                  fontWeight={500}
                  style={{ color: 'white', margin: 0 }}
                  fontSize={'0.8rem'}
                >
                  {CurrencySymbols[currencyCode as CurrencyCode]}
                </Typography>
              </Box>
              <Typography>{currencyCode}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}

const getCurrencyAdornment = (currency?: string) => {
  if (!currency) return undefined

  const symbol = CurrencySymbols[currency as CurrencyCode]
  if (!symbol) return undefined

  return <InputAdornment position="start">{symbol}</InputAdornment>
}

const PriceSection = ({
  title,
  description,
  currency,
  value,
  errors,
  onChange,
}: {
  title: string
  description: string
  currency: string
  value: OfflinePriceForm
  errors?: OfflinePriceFormErrors
  onChange: (field: keyof OfflinePriceForm, nextValue: string) => void
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#fbfcfe',
      }}
    >
      <Typography sx={{ fontWeight: 700, color: '#1d2a36' }}>
        {title}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mt: 0.5 }}>
        {description}
      </Typography>
      <Box
        sx={{
          mt: 2,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
        }}
      >
        <TextField
          label="Amount"
          type="number"
          value={value.price}
          onChange={(event) => onChange('price', event.target.value)}
          error={Boolean(errors?.price)}
          helperText={errors?.price}
          InputProps={{
            startAdornment: getCurrencyAdornment(currency),
          }}
          inputProps={{ min: 0, step: '0.01' }}
          fullWidth
        />
        <TextField
          label="VAT"
          type="number"
          value={value.vat}
          onChange={(event) => onChange('vat', event.target.value)}
          error={Boolean(errors?.vat)}
          helperText={errors?.vat}
          InputProps={{
            startAdornment: getCurrencyAdornment(currency),
          }}
          inputProps={{ min: 0, step: '0.01' }}
          fullWidth
        />
      </Box>
    </Paper>
  )
}

export default function OfflineBookingPage() {
  const theme = useTheme<Theme>()
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('lg'))
  const { communicationReference: communicationReferencePath } = useParams<{
    communicationReference?: string
  }>()
  const queryParams = useQueryParams()
  const rawDecision = queryParams.get('decision')?.toLowerCase()
  const decision = validDecisions.includes(rawDecision as OfflineDecision)
    ? (rawDecision as OfflineDecision)
    : undefined

  const communicationReference =
    communicationReferencePath ||
    queryParams.get('communicationReference') ||
    queryParams.get('reference') ||
    queryParams.get('ref') ||
    undefined

  const { data, isLoading, error } = useQuery<OfflineBookingData>({
    queryKey: ['offlineBooking', communicationReference],
    enabled: Boolean(communicationReference),
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async () => {
      const response = await consumerAPI.getBookingTracking(
        communicationReference!,
      )
      return response.data
    },
  })

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [decisionSubmitted, setDecisionSubmitted] = useState(false)
  const [completeForm, setCompleteForm] = useState<OfflineCompleteFormState>(
    createInitialCompleteFormState,
  )
  const [completeFormErrors, setCompleteFormErrors] =
    useState<OfflineCompleteFormErrors>({})
  const [hasAttemptedCompleteSubmit, setHasAttemptedCompleteSubmit] =
    useState(false)
  const acceptFiredRef = useRef(false)
  const [routeEstimate, setRouteEstimate] = useState<OfflineRouteEstimate>({
    estimatedJourneyTime: data?.journeyTime ?? null,
    estimatedJourneyDistance: data?.journeyDistance ?? null,
  })

  useEffect(() => {
    setRouteEstimate({
      estimatedJourneyTime: data?.journeyTime ?? null,
      estimatedJourneyDistance: data?.journeyDistance ?? null,
    })
  }, [data?.journeyDistance, data?.journeyTime])

  const decisionMutation = useMutation({
    mutationFn: ({
      decision: nextDecision,
      payload,
    }: {
      decision: OfflineDecision
      payload?: OfflineBookingCompletePayload
    }) =>
      consumerAPI.updateOfflineBooking(
        communicationReference!,
        nextDecision,
        payload,
      ),
    onSuccess: () => setDecisionSubmitted(true),
  })

  useEffect(() => {
    setConfirmDialogOpen(false)
    setDecisionSubmitted(false)
    setCompleteForm(createInitialCompleteFormState())
    setCompleteFormErrors({})
    setHasAttemptedCompleteSubmit(false)
    acceptFiredRef.current = false
  }, [communicationReference, decision])

  useEffect(() => {
    if (
      decision === 'accept' &&
      data &&
      communicationReference &&
      !acceptFiredRef.current
    ) {
      acceptFiredRef.current = true
      decisionMutation.mutate({ decision: 'accept' })
    }
  }, [decision, data, communicationReference, decisionMutation])

  const confirmationDecision =
    decision === 'decline' || decision === 'cancel' ? decision : undefined
  const isCompleteDecision = decision === 'complete'

  const updateCompleteForm = (
    updater: (current: OfflineCompleteFormState) => OfflineCompleteFormState,
  ) => {
    let nextState = completeForm

    setCompleteForm((current) => {
      nextState = updater(current)
      return nextState
    })

    if (hasAttemptedCompleteSubmit) {
      setCompleteFormErrors(getCompleteFormErrors(nextState))
    }
  }

  const handlePriceSectionChange = (
    section: PriceSectionKey,
    field: keyof OfflinePriceForm,
    nextValue: string,
  ) => {
    updateCompleteForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: nextValue,
      },
    }))
  }

  const handleCompletionStatusChange = (
    nextValue: OfflineBookingCompletionStatus,
  ) => {
    updateCompleteForm((current) => ({
      ...current,
      status: nextValue,
    }))
  }

  const handleCurrencyChange = (nextValue: string) => {
    updateCompleteForm((current) => ({
      ...current,
      currency: nextValue,
    }))
  }

  const handleJourneyMetricChange = (
    field: JourneyMetricKey,
    nextValue: string,
  ) => {
    updateCompleteForm((current) => ({
      ...current,
      [field]: nextValue,
    }))
  }

  const handleExtraChargeTypeChange = (
    index: number,
    nextValue: OfflineBookingAdditionalChargeTypeName,
  ) => {
    updateCompleteForm((current) => ({
      ...current,
      unknownExtras: current.unknownExtras.map((extra, extraIndex) =>
        extraIndex === index
          ? {
              ...extra,
              additionalChargeTypeName: nextValue,
            }
          : extra,
      ),
    }))
  }

  const handleExtraPriceChange = (
    index: number,
    field: keyof OfflinePriceForm,
    nextValue: string,
  ) => {
    updateCompleteForm((current) => ({
      ...current,
      unknownExtras: current.unknownExtras.map((extra, extraIndex) =>
        extraIndex === index
          ? {
              ...extra,
              price: {
                ...extra.price,
                [field]: nextValue,
              },
            }
          : extra,
      ),
    }))
  }

  const handleAddExtra = () => {
    updateCompleteForm((current) => ({
      ...current,
      unknownExtras: [...current.unknownExtras, createEmptyExtraForm()],
    }))
  }

  const handleRemoveExtra = (index: number) => {
    updateCompleteForm((current) => ({
      ...current,
      unknownExtras: current.unknownExtras.filter(
        (_, extraIndex) => extraIndex !== index,
      ),
    }))
  }

  const handleDecisionClick = () => setConfirmDialogOpen(true)

  const handleConfirm = () => {
    setConfirmDialogOpen(false)
    if (confirmationDecision) {
      decisionMutation.mutate({ decision: confirmationDecision })
    }
  }

  const handleCancelDialog = () => setConfirmDialogOpen(false)

  const handleCompleteSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setHasAttemptedCompleteSubmit(true)

    const errors = getCompleteFormErrors(completeForm)
    setCompleteFormErrors(errors)

    if (Object.keys(errors).length > 0 || !communicationReference) {
      return
    }

    decisionMutation.mutate({
      decision: 'complete',
      payload: buildCompletePayload(communicationReference, completeForm),
    })
  }

  const progressValue = data?.tracking?.progress ?? data?.progress
  const trackingStatusValue = data?.tracking?.status
  // const progressLabel = progressValue
  //   ? progressLabelLookup[progressValue] ||
  //     formatEnumFallbackLabel(progressValue)
  //   : 'Booking received'
  // const trackingStatusLabel = trackingStatusValue
  //   ? trackingStatusLabelLookup[trackingStatusValue] ||
  //     formatEnumFallbackLabel(trackingStatusValue)
  //   : undefined
  const pageTitle = decision ? pageTitleMap[decision] : 'Booking confirmed'
  const pageInstructions = decision ? pageInstructionsMap[decision] : undefined
  const hasSubmittedFinalStatus =
    isCompleteDecision &&
    (progressValue === 'Completed' || progressValue === 'No_Show')
  const showCompleteSubmissionAlert =
    isCompleteDecision &&
    (decisionMutation.isLoading || decisionSubmitted || hasSubmittedFinalStatus)
  const decisionMessage = confirmationDecision
    ? `Review the booking details and confirm if you want to ${confirmationDecision} this booking.`
    : decision
    ? decisionMessageMap[decision]
    : undefined
  const chipColors = decision
    ? decisionColorMap[decision]
    : { bg: 'rgba(237, 108, 2, 0.12)', color: '#ed0202' }
  const loadingMessage = isCompleteDecision
    ? 'Submitting journey details...'
    : `Submitting ${decision} decision...`
  const successMessage = isCompleteDecision
    ? 'The journey details have been submitted successfully.'
    : `The ${decision} decision has been submitted successfully.`
  const existingCompletionMessage =
    'The final pricing and status have already been submitted for this booking.'
  const errorMessage = isCompleteDecision
    ? 'Failed to submit the journey details. You may need to try again.'
    : `Failed to submit the ${decision} decision. You may need to try again.`
  const showDecisionFailureReasons =
    decision === 'accept' || decision === 'decline'
  const canDecline =
    progressValue === 'Allocated' && trackingStatusValue === 'Attention_Needed'
  const showDesktopMapLayout = isDesktopLayout && hasRoute(data)
  const displayedJourneyTime =
    routeEstimate.estimatedJourneyTime ?? data?.journeyTime
  const displayedJourneyDistance =
    routeEstimate.estimatedJourneyDistance ?? data?.journeyDistance

  const handleRouteInfoChange = (routeInfo: {
    estimatedJourneyTime: number
    estimatedJourneyDistance: number
  }) => {
    const estimatedJourneyTimeSeconds = routeInfo.estimatedJourneyTime * 60

    setRouteEstimate((current) => {
      if (
        current.estimatedJourneyTime === estimatedJourneyTimeSeconds &&
        current.estimatedJourneyDistance === routeInfo.estimatedJourneyDistance
      ) {
        return current
      }

      return {
        // HereRouteMap reports journey time in minutes; this page formats in seconds.
        estimatedJourneyTime: estimatedJourneyTimeSeconds,
        estimatedJourneyDistance: routeInfo.estimatedJourneyDistance,
      }
    })
  }

  const bookingDetailsSection = data && (
    <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: '8px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            sx={{
              fontSize: { xs: '1.8rem', sm: '1.8rem' },
              fontWeight: 700,
              color: '#1d2a36',
              textWrap: 'nowrap',
            }}
          >
            {pageTitle}
          </Typography>
          {pageInstructions &&
            !showCompleteSubmissionAlert &&
            !decisionSubmitted && (
              <Alert
                severity={decision !== 'complete' ? 'info' : 'warning'}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.95rem',
                  mt: 0.5,
                }}
              >
                {pageInstructions}
              </Alert>
            )}
          {isCompleteDecision && decisionMutation.isLoading && (
            <Alert
              severity="info"
              icon={<CircularProgress size={20} />}
              sx={{
                color: 'text.secondary',
                fontSize: '0.95rem',
                mt: 0.5,
              }}
            >
              {loadingMessage}
            </Alert>
          )}
          {isCompleteDecision &&
            !decisionMutation.isLoading &&
            (decisionSubmitted || hasSubmittedFinalStatus) && (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.95rem',
                  mt: 0.5,
                }}
              >
                {hasSubmittedFinalStatus && !decisionSubmitted
                  ? existingCompletionMessage
                  : successMessage}
              </Alert>
            )}
          {/* <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 1,
            }}
          >
            {progressLabel && (
              <Chip
                label={`${progressLabel}`}
                sx={{
                  backgroundColor: 'rgba(0, 38, 61, 0.1)',
                  color: theme.palette.brand.main,
                  fontWeight: 700,
                }}
              />
            )}
            {trackingStatusLabel && (
              <Chip
                label={`${trackingStatusLabel}`}
                variant="outlined"
                sx={{
                  borderColor: 'rgba(0, 38, 61, 0.24)',
                  color: theme.palette.brand.main,
                  fontWeight: 700,
                  backgroundColor: '#fff',
                }}
              />
            )}
          </Box> */}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        }}
      >
        <IconInfo
          Icon={InfoOutlinedIcon}
          title="Our job reference"
          value={data.jobReference || 'Not available'}
        />
        <IconInfo
          Icon={CalendarTodayOutlinedIcon}
          title="Pickup time"
          value={formatBookingWhen(data.when)}
        />
        <IconInfo
          Icon={TimerOutlinedIcon}
          title="Estimated journey time"
          value={formatJourneyTime(displayedJourneyTime ?? undefined)}
        />
        <IconInfo
          Icon={StraightenOutlinedIcon}
          title="Estimated distance"
          value={formatJourneyDistance(displayedJourneyDistance ?? undefined)}
        />
      </Box>
    </Paper>
  )

  const journeyDetailsSection = data && (
    <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: '8px' }}>
      <Typography
        sx={{
          fontSize: '0.9rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mb: 2,
        }}
      >
        Journey details
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        }}
      >
        <IconInfo
          Icon={PickupMarkerIcon}
          title="Pickup"
          value={formatAddress(data.pickup)}
        />
        <IconInfo
          Icon={DropoffMarkerIcon}
          title="Dropoff"
          value={formatAddress(data.dropOff)}
        />
      </Box>

      {data.additionalStops && data.additionalStops.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              mb: 2,
            }}
          >
            Additional stops
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 3,
              color: 'text.secondary',
              display: 'grid',
              gap: 1,
            }}
          >
            {data.additionalStops.map((stop, index) => (
              <Box component="li" key={`${formatAddress(stop)}-${index}`}>
                {formatAddress(stop)}
              </Box>
            ))}
          </Box>
        </>
      )}

      {data.specialInstructions && (
        <>
          <Divider sx={{ my: 2 }} />
          <IconInfo
            Icon={NotesOutlinedIcon}
            title="Special instructions"
            value={data.specialInstructions}
          />
        </>
      )}
    </Paper>
  )

  const mapSection = data && hasRoute(data) && (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: '8px',
        p: 0,
        ml: { xs: 0, md: 2 },
        minHeight: 'clamp(320px, 42vw, 520px)',
        height: showDesktopMapLayout ? '100%' : 'auto',
        alignSelf: showDesktopMapLayout ? 'stretch' : 'start',
        display: 'flex',
      }}
    >
      <DirectionsMap
        origin={data.pickup}
        destination={data.dropOff}
        additionalStops={data.additionalStops}
        mapHeight={showDesktopMapLayout ? '100%' : 'clamp(320px, 42vw, 520px)'}
        mapWidth="100%"
        updateRouteInfo={handleRouteInfoChange}
        mapOptions={{
          zoomControl: false,
          scrollWheelZoom: false,
          dragging: false,
          doubleClickZoom: false,
          touchZoom: false,
        }}
      />
    </Paper>
  )

  const passengerInfoSection = data && (
    <PassengerSummary passengerData={data.leadPassenger} />
  )

  const driverInfoSection = data && (
    <DriverSummary driver={data.tracking?.driver} />
  )

  const completionFormSection =
    isCompleteDecision && !decisionSubmitted && !hasSubmittedFinalStatus ? (
      <Paper
        component="form"
        elevation={0}
        onSubmit={handleCompleteSubmit}
        sx={{ p: { xs: 3, sm: 4 }, borderRadius: '8px' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '1.2rem',
                fontWeight: 700,
                color: '#1d2a36',
              }}
            >
              Journey details
            </Typography>
            <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
              Enter the final journey details below, including pricing, distance
              and whether to mark it as Complete or No Show.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          }}
        >
          <TextField
            label="Status"
            select
            value={completeForm.status}
            onChange={(event) =>
              handleCompletionStatusChange(
                event.target.value as OfflineBookingCompletionStatus,
              )
            }
            error={Boolean(completeFormErrors.status)}
            helperText={completeFormErrors.status}
            fullWidth
          >
            {completionStatusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <CurrencySelectField
            label="Currency"
            value={completeForm.currency}
            onChange={handleCurrencyChange}
            error={Boolean(completeFormErrors.currency)}
            helperText={completeFormErrors.currency}
          />
        </Box>

        <Box sx={{ mt: 3, display: 'grid', gap: 2 }}>
          <PriceSection
            title="Base price"
            description="Enter the final base price for this journey."
            currency={completeForm.currency}
            value={completeForm.driverPrice}
            errors={completeFormErrors.driverPrice}
            onChange={(field, nextValue) =>
              handlePriceSectionChange('driverPrice', field, nextValue)
            }
          />
          <PriceSection
            title="Waiting price"
            description="Capture any waiting-time charge applied to the job."
            currency={completeForm.currency}
            value={completeForm.waitingPrice}
            errors={completeFormErrors.waitingPrice}
            onChange={(field, nextValue) =>
              handlePriceSectionChange('waitingPrice', field, nextValue)
            }
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 3,
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fbfcfe',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#1d2a36' }}>
                Extras
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                  mt: 0.5,
                }}
              >
                Add any other charges that were applied outside the standard
                pricing sections.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddOutlinedIcon />}
              onClick={handleAddExtra}
            >
              Add extra charge
            </Button>
          </Box>

          {completeForm.unknownExtras.length === 0 && (
            <Typography sx={{ color: 'text.secondary', mt: 2 }}>
              No extra charges added.
            </Typography>
          )}

          <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
            {completeForm.unknownExtras.map((extra, index) => {
              const extraErrors = completeFormErrors.unknownExtras?.[index]

              return (
                <Paper
                  key={`unknown-extra-${index}`}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: '#fff',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      Extra charge {index + 1}
                    </Typography>
                    <Button
                      color="inherit"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => handleRemoveExtra(index)}
                    >
                      Remove
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    }}
                  >
                    <TextField
                      select
                      label="Charge type"
                      value={extra.additionalChargeTypeName}
                      onChange={(event) =>
                        handleExtraChargeTypeChange(
                          index,
                          event.target
                            .value as OfflineBookingAdditionalChargeTypeName,
                        )
                      }
                      error={Boolean(extraErrors?.additionalChargeTypeName)}
                      helperText={extraErrors?.additionalChargeTypeName}
                      fullWidth
                    >
                      {additionalChargeTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: '1fr 1fr 1fr',
                      },
                    }}
                  >
                    <TextField
                      label="Amount"
                      type="number"
                      value={extra.price.price}
                      onChange={(event) =>
                        handleExtraPriceChange(
                          index,
                          'price',
                          event.target.value,
                        )
                      }
                      error={Boolean(extraErrors?.price?.price)}
                      helperText={extraErrors?.price?.price}
                      InputProps={{
                        startAdornment: getCurrencyAdornment(
                          completeForm.currency,
                        ),
                      }}
                      inputProps={{ min: 0, step: '0.01' }}
                      fullWidth
                    />
                    <TextField
                      label="VAT"
                      type="number"
                      value={extra.price.vat}
                      onChange={(event) =>
                        handleExtraPriceChange(index, 'vat', event.target.value)
                      }
                      error={Boolean(extraErrors?.price?.vat)}
                      helperText={extraErrors?.price?.vat}
                      InputProps={{
                        startAdornment: getCurrencyAdornment(
                          completeForm.currency,
                        ),
                      }}
                      inputProps={{ min: 0, step: '0.01' }}
                      fullWidth
                    />
                  </Box>
                </Paper>
              )
            })}
          </Box>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 3,
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fbfcfe',
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#1d2a36' }}>
            Total Price:
          </Typography>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 3,
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fbfcfe',
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#1d2a36' }}>
            Journey details
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.9rem',
              mt: 0.5,
            }}
          >
            Record the final distance, duration and CO2 output for this trip.
          </Typography>

          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
            }}
          >
            <TextField
              label="Journey distance (miles)"
              type="number"
              value={completeForm.journeyDistance}
              onChange={(event) =>
                handleJourneyMetricChange('journeyDistance', event.target.value)
              }
              error={Boolean(completeFormErrors.journeyDistance)}
              helperText={completeFormErrors.journeyDistance}
              inputProps={{ min: 0, step: '0.01' }}
              fullWidth
            />
            <TextField
              label="Journey time (minutes)"
              type="number"
              value={completeForm.journeyTime}
              onChange={(event) =>
                handleJourneyMetricChange('journeyTime', event.target.value)
              }
              error={Boolean(completeFormErrors.journeyTime)}
              helperText={completeFormErrors.journeyTime}
              inputProps={{ min: 0, step: 1 }}
              fullWidth
            />
            <TextField
              label="CO2 emissions"
              type="number"
              value={completeForm.co2Emmissions}
              onChange={(event) =>
                handleJourneyMetricChange('co2Emmissions', event.target.value)
              }
              error={Boolean(completeFormErrors.co2Emmissions)}
              helperText={completeFormErrors.co2Emmissions}
              inputProps={{ min: 0, step: '0.01' }}
              fullWidth
            />
          </Box>
        </Paper>

        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            startIcon={<CheckCircleOutlineIcon />}
            loading={decisionMutation.isLoading}
            sx={{
              backgroundColor: chipColors.color,
              '&:hover': {
                backgroundColor: chipColors.color,
                opacity: 0.9,
              },
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Submit journey
          </LoadingButton>
        </Box>
      </Paper>
    ) : null

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'linear-gradient(180deg, rgba(0,38,61,0.08) 0%, #f7f9fc 28%, #ffffff 100%)',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: theme.palette.brand.main,
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: '0.85rem', letterSpacing: '0.12em' }}>
          OFFLINE BOOKING
        </Typography>
      </Box>

      <Box
        sx={{
          maxWidth: 1040,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 5 },
          display: 'grid',
          gap: 3,
        }}
      >
        {isLoading && communicationReference && (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: '8px',
              display: 'grid',
              gap: 2,
              placeItems: 'center',
            }}
          >
            <CircularProgress />
            <Typography>Fetching booking confirmation...</Typography>
          </Paper>
        )}

        {!communicationReference && (
          <Alert severity="warning">
            We cannot find a booking without a reference number
          </Alert>
        )}

        {error && !isLoading && (
          <Alert severity="error">
            Unable to confirm the booking information.
          </Alert>
        )}

        {data && !isLoading && (
          <>
            {decisionMessage &&
              !confirmationDecision &&
              !isCompleteDecision &&
              !decisionMutation.isLoading &&
              decisionMutation.isSuccess && (
                <Alert severity={decision === 'accept' ? 'success' : 'warning'}>
                  {decisionMessage}
                </Alert>
              )}

            {decisionMutation.isLoading && !isCompleteDecision && (
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                {loadingMessage}
              </Alert>
            )}
            {decisionSubmitted && !isCompleteDecision && (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              >
                {successMessage}
              </Alert>
            )}
            {decisionMutation.isError && (
              <Alert severity="error">
                <Typography sx={{ fontWeight: 700 }}>{errorMessage}</Typography>
                {showDecisionFailureReasons && (
                  <Box component="ul" sx={{ mb: 0, mt: 1, pl: 3 }}>
                    <Box component="li">
                      The supplier may have already accepted this job.
                    </Box>
                    <Box component="li">
                      The supplier may have already declined this job.
                    </Box>
                    <Box component="li">
                      The job may have already started or completed.
                    </Box>
                  </Box>
                )}
              </Alert>
            )}

            {confirmationDecision &&
              !decisionSubmitted &&
              (confirmationDecision === 'decline' && !canDecline ? (
                <Alert severity="info">
                  You have already declined this booking
                </Alert>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={decisionMutation.isLoading}
                  onClick={handleDecisionClick}
                  sx={{
                    backgroundColor: chipColors.color,
                    '&:hover': {
                      backgroundColor: chipColors.color,
                      opacity: 0.9,
                    },
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {decisionLabelMap[confirmationDecision]} this booking
                </Button>
              ))}

            {showDesktopMapLayout ? (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    alignItems: 'start',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg: 'minmax(0, 1.15fr) minmax(0, 0.85fr)',
                    },
                  }}
                >
                  <Box sx={{ display: 'grid', gap: 3 }}>
                    {bookingDetailsSection}
                    {journeyDetailsSection}
                    {passengerInfoSection}
                    {driverInfoSection}
                  </Box>
                  {mapSection}
                </Box>
                {completionFormSection}
              </>
            ) : (
              <>
                {bookingDetailsSection}
                {journeyDetailsSection}
                {mapSection}
                {passengerInfoSection}
                {driverInfoSection}
                {completionFormSection}
              </>
            )}
          </>
        )}
      </Box>

      {confirmationDecision && (
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCancelDialog}
          aria-labelledby="confirm-decision-title"
        >
          <DialogTitle id="confirm-decision-title">
            Confirm {decisionLabelMap[confirmationDecision]}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to {confirmationDecision} this booking?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDialog} startIcon={<CloseOutlined />}>
              No, go back
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              startIcon={<CancelOutlineIcon />}
              sx={{
                backgroundColor: chipColors.color,
                '&:hover': {
                  backgroundColor: chipColors.color,
                  opacity: 0.9,
                },
              }}
            >
              Yes, {confirmationDecision}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  )
}
